import React, { useState, useEffect } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import axios from "axios";
import theme from "assets/theme";
import routes from "routes";
import { useVisionUIController } from "context";
import Configurator from "examples/Configurator";
import SiteDashboard from "layouts/site-dashboard";
import NationalDashboard from "layouts/national-dashboard";
import AreaDashboard from "layouts/area-dashboard";
import DataExport from "layouts/data-export";
import PPM from "./layouts/PPM";
import PpmDataExport from "./layouts/PPM/PpmDataExport";
import StockItems from "./layouts/PPM/StockItems";
import EngineerPage from "./layouts/PPM/EngineerPage";
import UserManagement from "./layouts/admin/UserManagement";
import CompanyManagement from "./layouts/admin/CompanyManagement";
import SiteManagement from "./layouts/admin/SiteManagement";
import GatewayManagement from "./layouts/admin/GatewayManagement";
import RegionManagement from "./layouts/admin/RegionManagement";
import ProtectedRoute from "./ProtectedRoute";
import SectionListPage from "./layouts/PPM/SectionListPage";
import BreakdownTracker from "./layouts/Breakdown";
import BreakdownSectionList from "./layouts/Breakdown/BreakdownSectionList";
import TrainLoadDataExport from "./layouts/train-load-export/TrainloadDataExport";
import TrainLoadRecipients from "./layouts/train-load-users/TrainLoadRecipients";

export default function App() {
  const [controller] = useVisionUIController();
  const { direction, layout } = controller;
  const { pathname } = useLocation();
  const [regions, setRegions] = useState([]);
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    if (token && userId) {
      const fetchUserSites = async () => {
        try {
          const response = await axios.get(
            "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/sites",
            {
              headers: { Authorization: token, "Content-Type": "application/json" },
              params: { user_id: userId },
            }
          );
          setSites(response.data);
          const regionsResponse = await axios.get(
            "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/regions",
            {
              headers: { Authorization: token },
              params: { user_id: userId },
            }
          );
          const formattedRegions = (regionsResponse.data || []).map((region) => ({
            region_id: region[0],
            name: region[1],
            description: region[2],
            created_at: region[3],
            updated_at: region[4],
          }));
          setRegions(formattedRegions);
        } catch (error) {
          console.error("Error fetching sites:", error.message);
        }
      };

      fetchUserSites();
    }
  }, [token, userId]);

  const defaultSiteId = sites.length > 0 ? sites[0].site_id : null;

  useEffect(() => {
    if (defaultSiteId && pathname === "/site-dashboard") {
      window.location.href = `/site-dashboard/${defaultSiteId}`;
    }
  }, [defaultSiteId, pathname]);

  useEffect(() => {
    if (defaultSiteId && pathname === "/data-export") {
      window.location.href = `/data-export/${defaultSiteId}`;
    }
  }, [defaultSiteId, pathname]);

  useEffect(() => {
    if (defaultSiteId && pathname === "/ppm") {
      window.location.href = `/ppm/${defaultSiteId}`;
    }
  }, [defaultSiteId, pathname]);

  useEffect(() => {
    if (defaultSiteId && pathname === "/ppm/section-list") {
      window.location.href = `/ppm/${defaultSiteId}/section-list`;
    }
  }, [defaultSiteId, pathname]);

  useEffect(() => {
    if (defaultSiteId && pathname === "/engineer-page") {
      window.location.href = `/engineer-page/${defaultSiteId}`;
    }
  }, [defaultSiteId, pathname]);
  useEffect(() => {
    if (defaultSiteId && pathname === "/breakdown/section-list") {
      window.location.href = `/breakdown/${defaultSiteId}/section-list`;
    }
  }, [defaultSiteId, pathname]);
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (defaultSiteId && pathname === "/ppm-data-export") {
      window.location.href = `/ppm-data-export/${defaultSiteId}`;
    }
  }, [defaultSiteId, pathname]);

  useEffect(() => {
    if (defaultSiteId && pathname === "/train-load-export") {
      window.location.href = `/train-load-export/${defaultSiteId}`;
    }
  }, [defaultSiteId, pathname]);

  useEffect(() => {
    if (defaultSiteId && pathname === "/train-load-recipients") {
      navigate(`/train-load-recipients/${defaultSiteId}`);
    }
  }, [defaultSiteId, pathname, navigate]);

  useEffect(() => {
    if (pathname === "/area-dashboard" && regions.length > 0) {
      // Redirect to the first region's dashboard by default
      navigate(`/area-dashboard/${regions[0].region_id}`);
    }
  }, [pathname, regions]);

  const getRoutes = (routes) => {
    return routes.map((route) => {
      if (route.type === "collapse") {
        return (
          <Route
            key={route.key}
            path={route.route}
            element={
              <ProtectedRoute>
                <route.component />
              </ProtectedRoute>
            }
          />
        );
      }
      return null;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {layout === "site-dashboard" && <Configurator />}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route
          path="/data-export/:site_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 5]}>
              <DataExport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/area-dashboard/:region_id"
          element={
            <ProtectedRoute allowedRoles={[1, 3, 5]}>
              <AreaDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/area-dashboard" element={<Navigate to="/area-dashboard" />} />
        <Route
          path="/data-export"
          element={defaultSiteId ? <Navigate to={`/data-export/${defaultSiteId}`} /> : null}
        />
        <Route
          path="/ppm-data-export/:site_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 5]}>
              <PpmDataExport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ppm-data-export"
          element={defaultSiteId ? <Navigate to={`/ppm-data-export/${defaultSiteId}`} /> : null}
        />
        <Route
          path="/ppm/:site_id/:section_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 4, 5]}>
              <PPM />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ppm"
          element={defaultSiteId ? <Navigate to={`/ppm/${defaultSiteId}`} /> : null}
        />
        <Route
          path="/stock-items/:site_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 5]}>
              <StockItems />
            </ProtectedRoute>
          }
        />
        <Route
          path="/breakdown/:site_id/:section_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 4, 5]}>
              <BreakdownTracker /> {/* Or whatever component should handle this */}
            </ProtectedRoute>
          }
        />

        <Route
          path="/breakdown/:site_id/section-list/"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 4, 5]}>
              <BreakdownSectionList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/breakdown/section-list"
          element={
            defaultSiteId ? <Navigate to={`/breakdown/section-list/${defaultSiteId}`} /> : null
          }
        />
        <Route
          path="/breakdown"
          element={defaultSiteId ? <Navigate to={`/breakdown/${defaultSiteId}`} /> : null}
        />
        <Route
          path="/engineer-page/:site_id"
          element={
            <ProtectedRoute allowedRoles={[3, 4]}>
              <EngineerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer-page"
          element={defaultSiteId ? <Navigate to={`/engineer-page/${defaultSiteId}`} /> : null}
        />

        <Route
          path="/site-dashboard/:site_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 5]}>
              <SiteDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/site-dashboard"
          element={defaultSiteId ? <Navigate to={`/site-dashboard/${defaultSiteId}`} /> : null}
        />

        <Route
          path="/ppm/:site_id/section-list"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3, 4, 5]}>
              <SectionListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ppm/section-list"
          element={defaultSiteId ? <Navigate to={`/ppm/section-list/${defaultSiteId}`} /> : null}
        />

        <Route
          path="/train-load-export/:site_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3]}>
              <TrainLoadDataExport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train-load-export"
          element={defaultSiteId ? <Navigate to={`/train-load-export/${defaultSiteId}`} /> : null}
        />
        <Route
          path="/national-dashboard"
          element={
            <ProtectedRoute allowedRoles={[3, 5]}>
              <NationalDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train-load-recipients/:site_id"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3]}>
              <TrainLoadRecipients />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train-load-recipients"
          element={
            defaultSiteId ? <Navigate to={`/train-load-recipients/${defaultSiteId}`} /> : null
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={[3]}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies"
          element={
            <ProtectedRoute allowedRoles={[3]}>
              <CompanyManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sites"
          element={
            <ProtectedRoute allowedRoles={[3]}>
              <SiteManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/gateways"
          element={
            <ProtectedRoute allowedRoles={[3]}>
              <GatewayManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/regions"
          element={
            <ProtectedRoute allowedRoles={[3]}>
              <RegionManagement />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/site-dashboard" />} />
      </Routes>
    </ThemeProvider>
  );
}
